import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/ui/utils/style'
import { Link, LinkProps } from '../link/Link'
import { Ref } from 'react'

export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-xs border border-border border-opacity-10 font-semibold text-base ring-offset-background transition-[transform,color,background-color,border-color,opacity] duration-150 disabled:pointer-events-none active:scale-[0.97] disabled:bg-muted disabled:bg-opacity-60 disabled:text-secondary-foreground disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover hover:text-background',
        secondary: 'bg-primary text-primary-foreground hover:bg-primary-hover hover:text-background',
        yellow: 'bg-gradient-yellow text-background',
        yellowOutline:
          'border border-gradient-yellow bg-gradient-yellow/10 text-gradient-yellow hover:bg-gradient-yellow/20',
        text: 'border-none text-primary disabled:bg-transparent',
        icon: 'border-none',
        floating:
          'flex items-center justify-center gap-2 rounded-sm border border-white/4 bg-[#0F0F12] px-5 py-3 font-normal text-foreground text-xs shadow-md hover:bg-[#1A1A1D] hover:text-foreground',
        ghost:
          'border border-white/10 bg-white/[.03] text-dim-foreground hover:border-white/20 hover:text-muted-foreground',
        outline: 'border border-primary/10 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary',
      },
      rounded: {
        full: 'rounded-full',
        undefined: '',
      },
      size: {
        sm: 'h-8 gap-1 px-3 py-2 text-xs',
        md: 'h-10 gap-1.5 px-4 py-2',
        lg: 'h-14 gap-2.5 px-6 py-4',
        undefined: '',
      },
      spaceAround: {
        none: 'h-auto p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  prefixIcon?: React.ReactNode
  postfixIcon?: React.ReactNode
  ref?: Ref<HTMLButtonElement>
}

export function Button({
  className,
  variant,
  size,
  spaceAround,
  asChild = false,
  type = 'button',
  prefixIcon,
  postfixIcon,
  rounded,
  ref,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      {...props}
      className={cn(buttonVariants({ variant, size, spaceAround, rounded }), className)}
      ref={ref}
      type={type}
    >
      <>
        {prefixIcon}
        {props.children}
        {postfixIcon}
      </>
    </Comp>
  )
}

Button.displayName = 'Button'

export type LinkButtonProps = VariantProps<typeof buttonVariants> &
  LinkProps & {
    disabled?: boolean
    prefixIcon?: React.ReactNode
    postfixIcon?: React.ReactNode
    ref?: Ref<HTMLAnchorElement>
  }

export function LinkButton({
  className,
  variant,
  rounded,
  size,
  spaceAround,
  disabled,
  prefixIcon,
  postfixIcon,
  ref,
  ...props
}: LinkButtonProps) {
  return (
    <>
      {disabled ? (
        <Button disabled className={cn(buttonVariants({ variant, rounded, size, spaceAround, className }))}>
          {prefixIcon}
          {props.children}
          {postfixIcon}
        </Button>
      ) : (
        <Link className={cn(buttonVariants({ variant, rounded, size, spaceAround, className }))} ref={ref} {...props}>
          {prefixIcon}
          {props.children}
          {postfixIcon}
        </Link>
      )}
    </>
  )
}
